import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApiService } from '../models/adminApiService';

const SecurityMonitor = ({ isVisible = false }) => {
  const [securityStats, setSecurityStats] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (isVisible) {
      updateSecurityStats();
      const interval = setInterval(updateSecurityStats, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const updateSecurityStats = () => {
    try {
      const stats = adminApiService.getSecurityStats();
      setSecurityStats(stats);
      
      // Check for security alerts
      const newAlerts = [];
      
      if (stats.tokenExpiry && stats.tokenExpiry < 60 * 60 * 1000) { // Less than 1 hour
        newAlerts.push({
          id: 'token_expiring',
          type: 'warning',
          message: 'Authentication token expires soon',
          timestamp: Date.now()
        });
      }
      
      if (stats.needsRefresh) {
        newAlerts.push({
          id: 'needs_refresh',
          type: 'info',
          message: 'Token refresh recommended',
          timestamp: Date.now()
        });
      }
      
      if (stats.rateLimitRemaining < 10) {
        newAlerts.push({
          id: 'rate_limit_warning',
          type: 'warning',
          message: `Rate limit warning: ${stats.rateLimitRemaining} requests remaining`,
          timestamp: Date.now()
        });
      }
      
      if (stats.securityEvents > 0 && stats.lastSecurityEvent) {
        const lastEventTime = new Date(stats.lastSecurityEvent.timestamp).getTime();
        if (Date.now() - lastEventTime < 5 * 60 * 1000) { // Within last 5 minutes
          newAlerts.push({
            id: 'recent_security_event',
            type: 'error',
            message: `Recent security event: ${stats.lastSecurityEvent.event}`,
            timestamp: Date.now()
          });
        }
      }
      
      setAlerts(newAlerts);
    } catch (error) {
      console.error('Failed to update security stats:', error);
    }
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds || milliseconds <= 0) return 'Expired';
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getSecurityLevel = () => {
    if (!securityStats) return 'unknown';
    
    if (alerts.some(a => a.type === 'error')) return 'critical';
    if (alerts.some(a => a.type === 'warning')) return 'warning';
    if (!securityStats.hasValidSession) return 'critical';
    
    return 'secure';
  };

  const getSecurityColor = (level) => {
    switch (level) {
      case 'secure': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (!isVisible || !securityStats) return null;

  const securityLevel = getSecurityLevel();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="fixed top-20 right-4 bg-zinc-800 border border-zinc-700 rounded-lg p-4 max-w-sm z-50 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Security Monitor</h3>
          <div className={`w-3 h-3 rounded-full ${
            securityLevel === 'secure' ? 'bg-green-400' : 
            securityLevel === 'warning' ? 'bg-yellow-400' : 'bg-red-400'
          }`} />
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Session Status:</span>
            <span className={getSecurityColor(securityLevel)}>
              {securityStats.hasValidSession ? 'Active' : 'Invalid'}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-400">Token Expires:</span>
            <span className="text-white">
              {formatTime(securityStats.tokenExpiry)}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-400">Requests Remaining:</span>
            <span className="text-white">{securityStats.rateLimitRemaining}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-400">Security Events:</span>
            <span className="text-white">{securityStats.securityEvents}</span>
          </div>
        </div>
        
        {alerts.length > 0 && (
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <div className="text-xs font-semibold text-gray-400 mb-2">ALERTS</div>
            <div className="space-y-1">
              {alerts.map((alert) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`text-xs p-2 rounded ${
                    alert.type === 'error' ? 'bg-red-500/20 text-red-300' :
                    alert.type === 'warning' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-blue-500/20 text-blue-300'
                  }`}
                >
                  {alert.message}
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-3 pt-3 border-t border-zinc-700">
          <button
            onClick={updateSecurityStats}
            className="text-xs text-gray-400 hover:text-white transition-colors"
          >
            Refresh Security Status
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SecurityMonitor; 